import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import {
  uploadDocumentToDocumentManager,
  createDocumentRepositoryFile,
  getEmployeesRequest
} from '../../constants/requests';

/**
 * Dialog to add a file to the document repository.
 * 1. User selects a file and enters a name; we upload to document manager (POST /api/documents/).
 * 2. We use the returned _id as documentManagerId and create the repository file (current location).
 * Optional: accessControl (share with employees).
 */
export default function UploadFileDialog({
  open,
  onClose,
  repositoryId,
  currentFolderId,
  creatorId,
  employeeId,
  onSuccess
}) {
  const { request } = useLoading();
  const { showNotifications } = useNotifications();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [accessControl, setAccessControl] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setName('');
      setAccessControl([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  useEffect(() => {
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const response = await request(() => getEmployeesRequest());
        const list = response?.data?.body ?? response?.data?.data;
        if (Array.isArray(list)) {
          setEmployees(list);
        } else {
          setEmployees([]);
        }
      } catch (err) {
        setEmployees([]);
        showNotifications('error', 'Failed to load employees', 4000);
      } finally {
        setEmployeesLoading(false);
      }
    };

    // Fetch once when dialog is opened (and list isn't already loaded).
    if (open && employees.length === 0 && !employeesLoading) {
      fetchEmployees();
    }
    // eslint-disable-next-line
  }, [open]);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    if (selected && !name?.trim()) {
      setName(selected.name || '');
    }
  };

  const addAccessRow = () => {
    setAccessControl((prev) => [
      ...prev,
      { employeeId: '', accessLevel: 'view' }
    ]);
  };

  const removeAccessRow = (index) => {
    setAccessControl((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAccessRow = (index, field, value) => {
    setAccessControl((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name?.trim() ?? '';
    if (!file) {
      showNotifications('warning', 'Please select a file', 3000);
      return;
    }
    if (!trimmedName) {
      showNotifications('warning', 'Name is required', 3000);
      return;
    }
    if (!repositoryId || !creatorId || !employeeId) {
      showNotifications('error', 'Missing repository or user context', 3000);
      return;
    }

    const control = accessControl
      .filter((row) => (row.employeeId?.trim() ?? '') !== '')
      .map((row) => ({
        employeeId: row.employeeId.trim(),
        accessLevel: row.accessLevel || 'view'
      }));

    setSubmitting(true);
    try {
      // 1. Upload file to document manager
      const uploadResponse = await request(() =>
        uploadDocumentToDocumentManager(file, trimmedName)
      );

      const doc = uploadResponse?.data?.body ?? uploadResponse?.data;
      const documentManagerId =
        doc?._id ?? doc?.id ?? doc?.data?._id ?? doc?.data?.id;
      if (!documentManagerId) {
        showNotifications(
          'error',
          'Upload succeeded but no document ID returned',
          5000
        );
        setSubmitting(false);
        return;
      }

      // 2. Create file in document repository with the document manager _id
      const createResponse = await request(() =>
        createDocumentRepositoryFile(
          repositoryId,
          {
            documentManagerId: String(documentManagerId),
            creatorId,
            folderId: currentFolderId ?? null,
            accessControl: control.length > 0 ? control : undefined
          },
          employeeId
        )
      );

      if (createResponse?.data?.success && createResponse?.data?.data) {
        showNotifications('success', 'File added successfully', 3000);
        onClose();
        onSuccess?.();
      } else {
        showNotifications(
          'error',
          createResponse?.data?.message ?? 'Failed to add file to repository',
          5000
        );
      }
    } catch (err) {
      showNotifications(
        'error',
        err?.response?.data?.message ?? err?.message ?? 'Failed to add file',
        5000
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Upload file</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 0.5 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="*/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="upload-file-input"
            />
            <label htmlFor="upload-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={submitting}
                fullWidth
                sx={{ py: 1.5, mb: 1 }}
              >
                {file ? file.name : 'Choose file'}
              </Button>
            </label>
          </Box>

          <TextField
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            helperText="Display name for the document in Document Manager"
          />

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Share with (optional)
            </Typography>
            {accessControl.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={row.employeeId || ''}
                    label="Employee"
                    onChange={(e) =>
                      updateAccessRow(index, 'employeeId', e.target.value)
                    }
                    disabled={submitting || employeesLoading}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>
                        {employeesLoading
                          ? 'Loading employees…'
                          : 'Select employee'}
                      </em>
                    </MenuItem>
                    {employees.map((emp) => {
                      const id = emp?._id ?? emp?.id;
                      const fname = emp?.fname ?? '';
                      const lname = emp?.lname ?? '';
                      const label = `${fname} ${lname}`.trim();
                      return (
                        <MenuItem key={id} value={id}>
                          {label ? `${label} (${id})` : id}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Access</InputLabel>
                  <Select
                    value={row.accessLevel || 'view'}
                    label="Access"
                    onChange={(e) =>
                      updateAccessRow(index, 'accessLevel', e.target.value)
                    }
                    disabled={submitting}
                  >
                    <MenuItem value="view">View</MenuItem>
                    <MenuItem value="edit">Edit</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  onClick={() => removeAccessRow(index)}
                  disabled={submitting}
                  aria-label="Remove"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addAccessRow}
              disabled={submitting}
            >
              Add person
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
